import React, { CSSProperties, useEffect, useRef, useState } from 'react';

import { Picker } from 'emoji-mart';

import emojiIcon from 'assets/icons/emoji.svg';
import attachIcon from 'assets/icons/attachment.svg';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { getActiveChat, getRespondingMessage } from 'redux/chat/selectors';
import { getUser } from 'redux/authentication/auth.slice';
import { addReply, clearRespondingMessage, socketUpdate } from 'redux/chat/actions';
import socket from 'domain/services/socketIoTemp';
import { ReactComponent as SendIcon } from 'assets/icons/send.svg';
import {
  changeCurrentScreen,
  getCurrentScreenView,
} from 'redux/mobile_screen_flow/mobile_screen_flow.slice';

import 'emoji-mart/css/emoji-mart.css';
import './ChatInput.scoped.scss';
import { setRespondingClicked } from 'redux/chat/specific_chat.slices';

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
  setIsOnEdit?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChatInput: React.FC<Props> = ({ style, setIsOnEdit }) => {
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState<string>('');
  const activeChat = useAppSelector(getActiveChat);
  const respondingMessage = useAppSelector(getRespondingMessage);
  const user = useAppSelector(getUser);
  const currentScreenView = useAppSelector(getCurrentScreenView);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const inputFile = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    inputRef.current.focus();
  }, [respondingMessage]);

  const handleKeyDown = async e => {
    if ((e.key === 'Enter' || e.target.id === 'chat-send-button') && message.length > 0) {
      socket.emit('chatMessage', {
        chat_id: activeChat.id,
        body: message,
        responding: respondingMessage ?? null,
      });
      dispatch(clearRespondingMessage());
      setIsOnEdit(false);

      const newMessage = {
        body: message,
        user_id: user.id,
        user,
        responding: respondingMessage ?? null,
        replys: [],
        likes: [],
      };

      if (newMessage.responding) {
        dispatch(addReply(newMessage));
      }
      dispatch(changeCurrentScreen(currentScreenView));
      dispatch(socketUpdate());
      dispatch(setRespondingClicked({ id: null }));
      setMessage('');
    }
  };
  const handleEmoji = emoji => {
    setMessage(message + emoji);
  };

  const [file, setFile] = useState(null);

  const onButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  function handleUpload(event) {
    setFile(event.target.files[0]);
    // Add code here to upload file to server
    // ...
  }

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          event.target.id !== 'emoji-toggle'
        ) {
          setIsEmojiOpen(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideAlerter(wrapperRef);

  return (
    <>
      <div className="chat-input" style={style}>
        <div className="emoji-container">
          {isEmojiOpen && (
            <div ref={wrapperRef}>
              <Picker set="apple" onSelect={emoji => handleEmoji(emoji.native)} />
            </div>
          )}
        </div>
        {file !== null && (
          <div className="attachments">
            <label>{file?.name}</label>
            <label style={{ marginLeft: '10px', cursor: 'pointer' }} onClick={() => setFile(null)}>
              x
            </label>
          </div>
        )}
        {respondingMessage && (
          <div>
            <span>Responding to: {respondingMessage.body}</span>
            <span className="clear-responding" onClick={() => dispatch(clearRespondingMessage())}>
              x
            </span>
          </div>
        )}

        <div className="input-content">
          <div className="icon emoji pointer">
            <img
              id="emoji-toggle"
              onClick={() => setIsEmojiOpen(!isEmojiOpen)}
              src={emojiIcon}
              alt="emoji icons"
            />
          </div>

          <input
            type="text"
            ref={inputRef}
            value={message}
            onChange={({ target: { value } }) => setMessage(value)}
            onKeyDown={handleKeyDown}
          />
          <div className="icon plus pointer right-icons">
            <>
              <img src={attachIcon} alt="add more icons" onClick={onButtonClick} />
              <input
                type="file"
                id="file"
                ref={inputFile}
                onChange={event => handleUpload(event)}
                style={{ display: 'none' }}
              />
            </>

            <SendIcon className="send-icon" id="chat-send-button" onClick={handleKeyDown} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatInput;
